import React, { useMemo } from 'react';
import { Loading, StylovyzeDialog } from '@innovyze/stylovyze/lib/components';
import ExportButton from '@innovyze/stylovyze/lib/components/ExportButton';
import { EXPORT_STATUS, } from '@innovyze/stylovyze/lib/types/export.types';
import { Content, FittedLoader } from './ExportDialog.styles';
import useExportDialog from './handleExportRetrieval';
import { useGlobalization } from '@innovyze/stylovyze/lib/contexts/GlobalizationProvider';
/**
 * Export dialog
 */
export const ExportDialog = ({ open, setOpen, total, source, requestParams, maxRows = 100000, dataCy, exportResult, setExportResult, maxPollAttempts = 10, iterationTime, messages, }) => {
    const { t } = useGlobalization();
    const exporting = useMemo(() => {
        return exportResult.status === EXPORT_STATUS.LOADING;
    }, [exportResult]);
    useExportDialog({
        exportResult,
        setExportResult,
        maxPollAttempts,
        source,
        requestParams,
        iterationTime,
    });
    const message = useMemo(() => {
        const invalidMessage = messages?.invalid || (React.createElement(React.Fragment, null,
            React.createElement("p", null, t('This report has over {{rows}} records and cannot be exported.', {
                rows: maxRows?.toLocaleString(),
                context: 'CSV Export',
            })),
            React.createElement("p", null, t('Please apply filters to reduce the number of records in the report.', { context: 'CSV Export' }))));
        const idleMessage = messages?.idle ||
            t('You are about to export {{total}} records, would you like to continue?', { total, context: 'CSV Export' });
        if (total > maxRows)
            return invalidMessage;
        const successMessage = (React.createElement(React.Fragment, null,
            messages?.success || (React.createElement("p", null,
                React.createElement("b", null, t('Export Complete')))),
            React.createElement("p", null,
                React.createElement(ExportButton, { downloadUrl: exportResult?.downloadUrl ?? '', color: "primary", variant: "contained", onClick: () => undefined }, t('Download Report')))));
        const errorMessage = (React.createElement(React.Fragment, null,
            React.createElement("p", null, messages?.error || t('Export has failed')),
            React.createElement("p", null, exportResult?.message)));
        switch (exportResult.status) {
            case EXPORT_STATUS.INVALID:
                return invalidMessage;
            case EXPORT_STATUS.IDLE:
                return idleMessage;
            case EXPORT_STATUS.SUCCESS:
                return successMessage;
            case EXPORT_STATUS.ERROR:
                return errorMessage;
        }
    }, [
        total,
        maxRows,
        exportResult.status,
        exportResult?.downloadUrl,
        exportResult.message,
    ]);
    const disableExport = useMemo(() => total > maxRows, [total, maxRows]);
    const confirmText = useMemo(() => {
        if (!(exportResult.status !== EXPORT_STATUS.SUCCESS &&
            exportResult.status == EXPORT_STATUS.IDLE) ||
            disableExport) {
            return undefined;
        }
        return t('Export');
    }, [
        exporting,
        disableExport,
        exportResult?.downloadUrl,
        exportResult.status,
    ]);
    const title = useMemo(() => {
        if (exporting || exportResult?.downloadUrl)
            return t('Exporting to CSV');
        return t('Export to CSV');
    }, [exporting, exportResult]);
    const cancelText = useMemo(() => {
        if (exportResult?.status === EXPORT_STATUS.SUCCESS)
            return t('Close');
        return t('Cancel');
    }, [exportResult?.status]);
    return (React.createElement(StylovyzeDialog, { title: title, onCancel: () => {
            setExportResult(() => ({
                status: EXPORT_STATUS.IDLE,
            }));
            setOpen(false);
        }, onDialogClose: () => void 0, cancelText: cancelText, cancelButtonProps: {
            dataCy: 'export-dialog-cancel-button',
        }, confirmText: confirmText, confirmButtonProps: {
            disabled: disableExport,
            dataCy: 'export-dialog-export-button',
        }, onConfirm: () => {
            setExportResult(prev => ({
                ...prev,
                status: EXPORT_STATUS.STARTING,
            }));
        }, dataCy: dataCy, open: open },
        React.createElement(FittedLoader, null,
            React.createElement(Loading, { loading: exporting },
                React.createElement(Content, null, message)))));
};
export default ExportDialog;
